import { I18nService } from '@core/config/i18n.installer'
import { defineStore } from 'pinia'
import { getLanguageService } from '@/core/services/language.service.js'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import flagBr from '@/shared/assets/svg/flags/br.svg'
import flagDe from '@/shared/assets/svg/flags/de.svg'
import flagEl from '@/shared/assets/svg/flags/el.svg'
import flagEs from '@/shared/assets/svg/flags/es.svg'
import flagGb from '@/shared/assets/svg/flags/gb.svg'
import flagJp from '@/shared/assets/svg/flags/jp.svg'
import flagCn from '@/shared/assets/svg/flags/new-cn.svg'
import { formatLanguageLabel, getLanguageIsoCode } from '@/shared/lang/language-registry'

const LOCALE_META = {
  'en': { flag: flagGb, label: 'common.languages.en' },
  'pt-br': { flag: flagBr, label: 'common.languages.pt' },
  'es-la': { flag: flagEs, label: 'common.languages.es' },
  'zh-cn': { flag: flagCn, label: 'common.languages.zh' },
  'el': { flag: flagEl, label: 'common.languages.el' },
  'de': { flag: flagDe, label: 'common.languages.de' },
  'ja': { flag: flagJp, label: 'common.languages.ja' },
}

function withLanguageIdentity(locale) {
  return {
    ...locale,
    displayLabel: formatLanguageLabel(locale.key) ?? locale.name ?? locale.key,
    isoCode: getLanguageIsoCode(locale.key) ?? locale.key,
  }
}

export const useLanguageStore = defineStore('language', {
  state: () => ({
    currentLanguage: I18nService.getInitialLocale(),
    languageCounter: 0,
    availableLocales: [
      { label: 'common.languages.en', key: 'en', name: 'EN', flag: flagGb },
      { label: 'common.languages.pt', key: 'pt-br', name: 'PT', flag: flagBr },
      { label: 'common.languages.es', key: 'es-la', name: 'ES', flag: flagEs },
      { label: 'common.languages.zh', key: 'zh-cn', name: 'ZH', flag: flagCn },
      { label: 'common.languages.el', key: 'el', name: 'EL', flag: flagEl },
      { label: 'common.languages.de', key: 'de', name: 'DE', flag: flagDe },
      { label: 'common.languages.ja', key: 'ja', name: 'JA', flag: flagJp },
    ].map(withLanguageIdentity),
  }),

  getters: {
    getCurrentLanguage: state => state.currentLanguage,

    getTenantDefaultLanguage() {
      const authStore = useAuthStore()
      return (
        authStore.isAuthenticated
        && authStore.tenantData?.settings?.find(
          setting => setting.key === 'defaultLanguage',
        )?.value
      )
    },

    getActiveLocales(state) {
      const authStore = useAuthStore()
      const enabledLanguagesSetting = authStore.tenantData?.settings?.find(
        setting => setting.key === 'enabledLanguages',
      )
      if (!enabledLanguagesSetting?.value?.length) {
        return state.availableLocales
      }
      return state.availableLocales.filter(locale =>
        enabledLanguagesSetting.value.includes(locale.key),
      )
    },

    isSingleLanguage() {
      return this.getActiveLocales.length === 1
    },

    getEffectiveLanguage() {
      return (
        localStorage.getItem(I18nService.STORAGE_KEY)
        || this.getTenantDefaultLanguage
        || I18nService.getBrowserLocale()
      )
    },
  },

  actions: {
    incrementLanguageCounter() {
      this.languageCounter++
    },

    async fetchAvailableLocales() {
      try {
        const service = getLanguageService()
        const locales = await service.getAvailableLanguages()
        this.availableLocales = locales.map(l => withLanguageIdentity({
          ...l,
          ...(LOCALE_META[l.key] ?? { flag: null, label: `common.languages.${l.key}` }),
        }))
      }
      catch {
        // grace-fail: keep existing locale list
      }
    },

    async setLanguage(newLanguage) {
      try {
        if (!I18nService.SUPPORTED_LOCALES.includes(newLanguage)) {
          console.warn(`Unsupported locale: ${newLanguage}`)
          newLanguage = I18nService.FALLBACK_LOCALE
        }

        // const messages = I18nService.loadTranslationsForLocale(newLanguage)
        // i18n.global.setLocaleMessage(newLanguage, messages[newLanguage])
        // i18n.global.locale.value = newLanguage
        this.currentLanguage = newLanguage

        localStorage.setItem(I18nService.STORAGE_KEY, newLanguage)

        // this.incrementLanguageCounter()
        return true
      }
      catch (error) {
        console.error('Failed to set language:', error)
        return false
      }
    },

    async resetToDefault() {
      localStorage.removeItem(I18nService.STORAGE_KEY)

      const defaultLanguage = this.getEffectiveLanguage
      return this.setLanguage(defaultLanguage)
    },

    async initialize() {
      const authStore = useAuthStore()
      // Skip if tenant data not loaded yet — getActiveLocales would return all 5,
      // making every locale appear valid and preventing the fallback from running.
      // The watch in each layout will call initialize() again once tenantData is set.
      if (!authStore.tenantData)
        return

      const effectiveLanguage = this.getEffectiveLanguage
      const activeLocales = this.getActiveLocales

      const isActive = activeLocales.some(l => l.key === effectiveLanguage)
      const resolvedLanguage = isActive
        ? effectiveLanguage
        : activeLocales[0]?.key ?? effectiveLanguage

      if (!isActive || resolvedLanguage !== this.currentLanguage) {
        await this.setLanguage(resolvedLanguage)
        // vue-i18n locale was fixed at boot time from localStorage. When the
        // locale was forcibly corrected (not a user choice), a reload is needed
        // so the next boot picks up the corrected locale — same pattern as the
        // language switcher's location.reload() on user-triggered changes.
        if (!isActive) {
          globalThis.location.reload()
        }
      }
    },
  },
})
